// Pure helpers for humanizing `ExtractedLeadV2` field keys + values so the
// merge conflict modal can show "First name" instead of `first_name` and
// "(555) 555-1234" instead of "+15555551234".
//
// Pure / framework-agnostic so the same helpers can be reused elsewhere
// (audit views, history tab, etc.).

const FIELD_LABELS: Record<string, string> = {
  first_name: "First name",
  last_name: "Last name",
  phone: "Phone",
  email: "Email",
  product_line: "Product line",
  product_subtype: "Product subtype",
  size_config: "Size config",
  size_options: "Size options",
  product_interest_category: "Product interest category",
  product_interest_summary: "Product interest summary",
  enunciated_budget: "Stated budget",
  lead_source_category: "Lead source",
  lead_source_detail: "Lead source detail",
  delivery_zip: "Delivery ZIP",
  delivery_city: "Delivery city",
  delivery_state: "Delivery state",
  delivery_address: "Delivery address",
  best_time_to_contact: "Best time to contact",
  interest_level: "Interest level",
  notes: "Notes",
};

export function humanizeLeadField(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  // Fallback: snake_case → Title Case.
  return key
    .split("_")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}

/**
 * Display formatter for an extracted-lead value. Strings render as-is, arrays
 * as a bulleted list, phones get parenthesized formatting, null/empty become
 * a visible "(empty)" marker so the modal makes "I cleared this" obvious.
 */
export function formatLeadFieldValue(field: string, value: unknown): string {
  if (value == null || value === "") return "(empty)";
  if (field === "phone" && typeof value === "string") return formatPhoneForDisplay(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "(empty)";
    return value.map((v) => (v == null ? "(empty)" : String(v))).join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatPhoneForDisplay(raw: string): string {
  const digits = raw.replace(/\D+/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}
